import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faRightFromBracket, faRotate } from "@fortawesome/free-solid-svg-icons";
import { useStaffAuth } from "../store/staff";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15 disabled:bg-slate-100";
const labelCls =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500";

/** Staff profile & settings — /api/staff/me + auth actions. */
const StaffProfile = () => {
  const {
    staffProfile,
    staffProfileLoading,
    staffProfileError,
    fetchStaffProfile,
    updateStaffProfile,
    staffChangePassword,
    staffLogout,
    staffRefresh,
    staffAuthActionLoading,
    staffAuthActionError,
  } = useStaffAuth();

  const [form, setForm] = useState({ name: "", email: "", phone: "", avatar: "" });
  const [saved, setSaved] = useState("");

  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [pwOk, setPwOk] = useState(true);

  // GET /api/staff/me
  useEffect(() => {
    (async () => {
      const res = await fetchStaffProfile();
      if (res.success && res.profile) {
        const p = res.profile;
        setForm({
          name: p.name || [p.first_name, p.last_name].filter(Boolean).join(" ") || "",
          email: p.email || "",
          phone: p.phone || p.phoneNumber || "",
          avatar: p.avatar || p.image || "",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PATCH /api/staff/me
  const handleSave = async (e) => {
    e.preventDefault();
    setSaved("");
    // PATCH /api/staff/me is strict: name must be 2-100 chars, phone/avatar are
    // nullable, so empty optional values are sent as null and blank name is omitted.
    const payload = {};
    if (form.name.trim()) payload.name = form.name.trim();
    payload.phone = form.phone.trim() ? form.phone.trim() : null;
    payload.avatar = form.avatar.trim() ? form.avatar.trim() : null;
    const res = await updateStaffProfile(payload);
    if (res.success) setSaved("Profile updated successfully.");
  };

  // POST /api/staff/auth/change-password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg("");
    if (!pw.currentPassword || !pw.newPassword) {
      setPwOk(false);
      setPwMsg("Please fill in both password fields.");
      return;
    }
    if (pw.newPassword !== pw.confirmPassword) {
      setPwOk(false);
      setPwMsg("New password and confirmation do not match.");
      return;
    }
    const res = await staffChangePassword({
      currentPassword: pw.currentPassword,
      newPassword: pw.newPassword,
    });
    if (res.success) {
      setPwOk(true);
      setPwMsg("Password changed successfully.");
      setPw({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } else {
      setPwOk(false);
      setPwMsg(res.error || "Failed to change password.");
    }
  };

  const handleLogout = async () => {
    await staffLogout();
    window.location.href = "/staff/login";
  };

  return (
    <div>
      <StaffNavBar title="Profile" context="Manage your staff profile and password." />
      <AdminPageBody>
        {staffProfileLoading && !staffProfile ? (
          <AdminLoadingState />
        ) : (
          <>
            {staffProfileError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {staffProfileError}
              </div>
            )}

            <AdminPanel className="p-6">
              <h3 className="m-0 text-base font-semibold tracking-tight text-slate-900">
                Staff Profile
              </h3>
              <p className="mt-1 mb-5 text-sm text-slate-500">Your account details.</p>

              {saved && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-700">
                  {saved}
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input type="email" className={inputCls} value={form.email} disabled />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Full Name</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Avatar URL</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.avatar}
                    onChange={(e) => setForm((f) => ({ ...f, avatar: e.target.value }))}
                    placeholder="https://example.com/avatar.jpg"
                  />
                </div>
                <div className="flex justify-end">
                  <button type="submit" className={adminPrimaryButton} disabled={staffProfileLoading}>
                    {staffProfileLoading ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </form>
            </AdminPanel>

            <AdminPanel className="p-6">
              <h3 className="m-0 text-base font-semibold tracking-tight text-slate-900">
                Change Password
              </h3>
              <p className="mt-1 mb-5 text-sm text-slate-500">
                Use your current password to set a new one.
              </p>

              {pwMsg && (
                <div
                  className={`mb-4 rounded-lg border px-3 py-2.5 text-sm ${
                    pwOk
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  {pwMsg}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className={labelCls}>Current Password</label>
                  <input
                    type="password"
                    className={inputCls}
                    value={pw.currentPassword}
                    onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>New Password</label>
                    <input
                      type="password"
                      className={inputCls}
                      value={pw.newPassword}
                      onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Confirm New Password</label>
                    <input
                      type="password"
                      className={inputCls}
                      value={pw.confirmPassword}
                      onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="submit" className={adminPrimaryButton} disabled={staffAuthActionLoading}>
                    <FontAwesomeIcon icon={faKey} />{" "}
                    {staffAuthActionLoading ? "Saving…" : "Change Password"}
                  </button>
                </div>
              </form>
            </AdminPanel>

            <AdminPanel className="p-6">
              <h3 className="m-0 text-base font-semibold tracking-tight text-slate-900">
                Session
              </h3>
              <p className="mt-1 mb-5 text-sm text-slate-500">
                Refresh your access token or sign out of the staff panel.
              </p>
              {staffAuthActionError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  {staffAuthActionError}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
                  disabled={staffAuthActionLoading}
                  onClick={() => staffRefresh()}
                >
                  <FontAwesomeIcon icon={faRotate} /> Refresh session
                </button>
                <button
                  type="button"
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faRightFromBracket} /> Sign out
                </button>
              </div>
            </AdminPanel>
          </>
        )}
      </AdminPageBody>
    </div>
  );
};

export default StaffProfile;
